import { useEffect, useMemo, useState } from "react"
import Sidebar from "@/components/Sidebar"
import { useAuth } from "@/contexts/AuthContext"
import { Card } from "@/components/ui/card"
import { adService, userService } from "@/api/services"
import type { AdResponseDto } from "@/api/types/ad.types"
import { Loader2, Users, Package, BadgeCheck, Star, ArrowUp, Zap } from "lucide-react"

export default function DashboardPage() {
  const { user } = useAuth()

  // Combine firstName and lastName for display
  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : ""

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState("")

  const [ads, setAds] = useState<AdResponseDto[]>([])
  const [userStats, setUserStats] = useState<{
    basicTotal: number
    basicActive: number
    companyTotal: number
    companyActive: number
  } | null>(null)

  const isCompanyAd = (ad: AdResponseDto) => Boolean(ad.companyName || ad.companySlug)

  const isPromotionActive = (until?: string | null) => {
    if (!until) return false
    const t = new Date(until).getTime()
    return Number.isFinite(t) && t > Date.now()
  }

  const loadAdsAdminAll = async () => {
    const all: AdResponseDto[] = []
    let cursor: string | null | undefined = undefined
    let hasMore = true
    let prevCursor: string | null | undefined = undefined

    // Cursor pagination – fetch until API says no more.
    while (hasMore) {
      const response = await adService.getAds({ cursor: cursor || undefined, limit: 1000 })
      all.push(...response.data)

      prevCursor = cursor
      cursor = response.cursor ?? null
      hasMore = Boolean(response.hasMore)

      // Safety guards
      if (!hasMore) break
      if (!cursor) break
      if (cursor === prevCursor) break
      if (response.data.length === 0) break
    }

    return all
  }

  const loadUserStats = async () => {
    // We only need totals; API returns `total` so we request minimal page size.
    const [basicTotal, basicActive, companyTotal, companyActive] = await Promise.all([
      userService.getUsers({ page: 1, limit: 1, userType: "Basic" }),
      userService.getUsers({ page: 1, limit: 1, userType: "Basic", isActive: true }),
      userService.getUsers({ page: 1, limit: 1, userType: "Company" }),
      userService.getUsers({ page: 1, limit: 1, userType: "Company", isActive: true }),
    ])

    return {
      basicTotal: basicTotal.total || 0,
      basicActive: basicActive.total || 0,
      companyTotal: companyTotal.total || 0,
      companyActive: companyActive.total || 0,
    }
  }

  useEffect(() => {
    let isMounted = true

    const run = async () => {
      setLoading(true)
      setError("")
      try {
        const [allAds, stats] = await Promise.all([loadAdsAdminAll(), loadUserStats()])
        if (!isMounted) return
        setAds(allAds)
        setUserStats(stats)
      } catch (err: any) {
        if (!isMounted) return
        const msg = err.response?.data?.message || err.message || "Failed to load dashboard data"
        setError(msg)
      } finally {
        if (!isMounted) return
        setLoading(false)
      }
    }

    run()

    return () => {
      isMounted = false
    }
  }, [])

  const adStats = useMemo(() => {
    const totalAds = ads.length
    const companyAds = ads.filter(isCompanyAd).length
    const basicAds = totalAds - companyAds

    const byOwner = (predicate: (ad: AdResponseDto) => boolean) => ads.filter(predicate)
    const basic = byOwner((a) => !isCompanyAd(a))
    const company = byOwner((a) => isCompanyAd(a))

    const countPromo = (
      list: AdResponseDto[],
      flag: keyof Pick<AdResponseDto, "isTop" | "isUrgent" | "isHighlight" | "isHomepageHighlight">,
      untilKey: keyof Pick<AdResponseDto, "topUntil" | "urgentUntil" | "highlightUntil" | "homepageHighlightUntil">
    ) => {
      const total = list.filter((a) => Boolean(a[flag])).length
      const active = list.filter((a) => Boolean(a[flag]) && isPromotionActive(a[untilKey] as any)).length
      return { total, active }
    }

    const promos = {
      homepageHighlight: {
        overall: countPromo(ads, "isHomepageHighlight", "homepageHighlightUntil"),
        basic: countPromo(basic, "isHomepageHighlight", "homepageHighlightUntil"),
        company: countPromo(company, "isHomepageHighlight", "homepageHighlightUntil"),
      },
      highlight: {
        overall: countPromo(ads, "isHighlight", "highlightUntil"),
        basic: countPromo(basic, "isHighlight", "highlightUntil"),
        company: countPromo(company, "isHighlight", "highlightUntil"),
      },
      top: {
        overall: countPromo(ads, "isTop", "topUntil"),
        basic: countPromo(basic, "isTop", "topUntil"),
        company: countPromo(company, "isTop", "topUntil"),
      },
      urgent: {
        overall: countPromo(ads, "isUrgent", "urgentUntil"),
        basic: countPromo(basic, "isUrgent", "urgentUntil"),
        company: countPromo(company, "isUrgent", "urgentUntil"),
      },
    }

    return {
      totalAds,
      basicAds,
      companyAds,
      promos,
    }
  }, [ads])

  const topCards = [
    { label: "All Ads", value: adStats.totalAds.toString(), icon: Package, color: "text-primary" },
    { label: "Basic Ads", value: adStats.basicAds.toString(), icon: Users, color: "text-muted-foreground" },
    { label: "Company Ads", value: adStats.companyAds.toString(), icon: Users, color: "text-muted-foreground" },
  ]

  return (
    <div className="flex">
      <Sidebar />
      <main className="flex-1 ml-64 p-8 bg-background min-h-screen">
        <div className="max-w-6xl">
          <h1 className="text-3xl font-bold text-foreground mb-2">Dashboard</h1>
          <p className="text-muted-foreground mb-8">Welcome back, {userName}</p>

          {loading ? (
            <Card className="p-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </Card>
          ) : (
            <>
              {error && (
                <div className="bg-destructive/10 border border-destructive/30 rounded-lg p-3 mb-6">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                {topCards.map((stat) => {
                  const Icon = stat.icon
                  return (
                    <Card key={stat.label} className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-muted-foreground">{stat.label}</h3>
                        <Icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                      <p className="text-3xl font-bold text-foreground">{stat.value}</p>
                    </Card>
                  )
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground">Users</h3>
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  {userStats ? (
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Basic</p>
                        <p className="text-sm font-medium text-foreground">
                          {userStats.basicActive} active / {userStats.basicTotal} total
                        </p>
                      </div>
                      <div className="flex items-center justify-between">
                        <p className="text-sm text-muted-foreground">Company</p>
                        <p className="text-sm font-medium text-foreground">
                          {userStats.companyActive} active / {userStats.companyTotal} total
                        </p>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No data</p>
                  )}
                </Card>

                <Card className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-muted-foreground">Promotions</h3>
                    <BadgeCheck className="w-5 h-5 text-primary" />
                  </div>
                  <div className="space-y-3 text-sm">
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground flex items-center gap-2">
                        <Star className="h-4 w-4" /> Homepage Highlight
                      </p>
                      <p className="font-medium text-foreground">
                        {adStats.promos.homepageHighlight.overall.active} active /{" "}
                        {adStats.promos.homepageHighlight.overall.total} total
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground flex items-center gap-2">
                        <BadgeCheck className="h-4 w-4" /> Highlight
                      </p>
                      <p className="font-medium text-foreground">
                        {adStats.promos.highlight.overall.active} active /{" "}
                        {adStats.promos.highlight.overall.total} total
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground flex items-center gap-2">
                        <ArrowUp className="h-4 w-4" /> Top
                      </p>
                      <p className="font-medium text-foreground">
                        {adStats.promos.top.overall.active} active / {adStats.promos.top.overall.total}{" "}
                        total
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <p className="text-muted-foreground flex items-center gap-2">
                        <Zap className="h-4 w-4" /> Urgent
                      </p>
                      <p className="font-medium text-foreground">
                        {adStats.promos.urgent.overall.active} active /{" "}
                        {adStats.promos.urgent.overall.total} total
                      </p>
                    </div>
                  </div>
                </Card>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {(
                  [
                    { key: "homepageHighlight", title: "Homepage Highlight", icon: Star },
                    { key: "highlight", title: "Highlight", icon: BadgeCheck },
                    { key: "top", title: "Top", icon: ArrowUp },
                    { key: "urgent", title: "Urgent", icon: Zap },
                  ] as const
                ).map((p) => {
                  const promo = adStats.promos[p.key]
                  const Icon = p.icon
                  return (
                    <Card key={p.key} className="p-6">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-sm font-medium text-muted-foreground">{p.title} Breakdown</h3>
                        <Icon className="w-5 h-5 text-primary" />
                      </div>
                      <div className="space-y-3 text-sm">
                        <div className="flex items-center justify-between">
                          <p className="text-muted-foreground">Overall</p>
                          <p className="font-medium text-foreground">
                            {promo.overall.active} active / {promo.overall.total} total
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-muted-foreground">Basic ads</p>
                          <p className="font-medium text-foreground">
                            {promo.basic.active} active / {promo.basic.total} total
                          </p>
                        </div>
                        <div className="flex items-center justify-between">
                          <p className="text-muted-foreground">Company ads</p>
                          <p className="font-medium text-foreground">
                            {promo.company.active} active / {promo.company.total} total
                          </p>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  )
}
